import React, { useEffect, useState } from "react";
import { Form, Input, Button, Select, DatePicker, message } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { State, City } from "country-state-city";
import { createSurvey, updateSurvey } from "../../../api/CommonRequest";
import { useNavigate } from "react-router-dom";
const { Option } = Select;

const CreateSurvey = ({ editData }) => {

  const navigate = useNavigate()
  const [form] = Form.useForm();

  const [btnLoader, setBtnLoader] = useState(false);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const indiaCode = "IN";

  const states = State.getStatesOfCountry(indiaCode);
  const districts = selectedState
    ? City.getCitiesOfState(indiaCode, selectedState)
    : [];
  const cities = selectedDistrict
    ? City.getCitiesOfState(indiaCode, selectedState).filter(
      (city) => city.name !== selectedDistrict
    )
    : [];

  useEffect(() => {


    if (editData?.state) {
      const stateObj = states.find(
        (state) => state.name === editData.state
      );

      if (stateObj) {
        setSelectedState(stateObj.isoCode);
        form.setFieldsValue({ state: stateObj.isoCode });
      }
    }

    if (editData?.district) {
      setSelectedDistrict(editData.district);
      form.setFieldsValue({ district: editData.district });
    }

    if (editData?.city) {
      setSelectedCity(editData.city);
      form.setFieldsValue({ city: editData.city });
    }

    form.setFieldsValue({
      clientName: editData?.clientName,
      phonePrimary: editData?.phonePrimary,
      phoneSecondary: editData?.phoneSecondary
        ? editData?.phoneSecondary
        : undefined,
      clientEmail: editData?.clientEmail,
      startDate: moment(editData?.startDate),
      address: editData?.address,
      locationUrl: editData?.locationUrl,
      instructions: editData?.instructions,
    });


  }, [editData, form]);

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedDistrict(null);
    setSelectedCity(null);
    form.setFieldsValue({ district: null, city: null });
  };

  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedCity(null);
    form.setFieldsValue({ city: null });
  };

  const handleCityChange = (value) => {
    setSelectedCity(value);
  };

  const validatePhoneNumber = (_, value) => {
    const phoneRegex = /^[6-9]\d{9}$/; // Indian phone number format
    if (!value || phoneRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject("Please enter a valid 10-digit Indian phone number");
  };

  // const validateGoogleMapUrl = (_, value) => {
  //   const urlRegex = /^https:\/\/(www\.)?google\.com\/maps/;
  //   if (urlRegex.test(value)) {
  //     return Promise.resolve();
  //   }
  //   return Promise.reject("Please enter a valid Google Maps URL");
  // };

  const validateGoogleMapUrl = (_, value) => {
    const urlRegex = /^https:\/\/((www\.)?google\.com\/maps|maps\.app\.goo\.gl)/;
    if (urlRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject("Please enter a valid Google Maps URL");
  };

  const onFinish = async (values) => {
    try {
      setBtnLoader(true);
      const surveyData = {
        ...values,
        startDate: values.startDate.toISOString(),
        state: State.getStateByCodeAndCountry(values.state, indiaCode)?.name,
      };

      const { data } = editData
        ? await updateSurvey(editData._id, surveyData)
        : await createSurvey(surveyData);
      if (data.success) {
        message.success(data.message);
        setBtnLoader(false);
        navigate('/survey-list')
      }
    } catch (error) {
      console.error("Error processing survey:", error);
      message.error("Failed to process survey");
    } finally {
      setBtnLoader(false);
    }
  };

  return (
    <div className="flex-grow overflow-y-auto scrollbar-hide p-4 lg:p-6 md:p-5">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="text-left w-full">
            <h1 className="text-2xl font-bold text-gray-800">
              {editData ? "Edit Survey" : "Create New Survey"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Fill in the details below to {editData ? "update" : "schedule a new"} survey.
            </p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <Button
              onClick={() => navigate("/survey-list")}
              className="flex-1 md:flex-none h-10 px-6 rounded-lg font-medium hover:bg-gray-50 transition-all border-gray-200 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={() => form.submit()}
              loading={btnLoader}
              className="flex-1 md:flex-none h-10 px-8 rounded-lg font-bold bg-[#1955EB] text-white hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-200 active:scale-95 border-none"
            >
              {editData ? "Update Survey" : "Create Survey"}
            </Button>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Details Section */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-50 text-[#1955EB] flex items-center justify-center font-bold text-sm">1</div>
                <h2 className="text-lg font-bold text-gray-800">Basic Details</h2>
              </div>

              <Form.Item
                name="clientName"
                label="Client Name"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter client name" },
                  { min: 2, message: "Name must be at least 2 characters long" },
                  { max: 50, message: "Name cannot exceed 50 characters" },
                ]}
              >
                <Input className="h-10 rounded-lg border-gray-200 focus:border-[#1955EB] transition-all" placeholder="Enter full name" />
              </Form.Item>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  name="phonePrimary"
                  label="Contact Number"
                  rules={[
                    { required: true, message: "Please enter contact number" },
                    { validator: validatePhoneNumber },
                  ]}
                >
                  <Input className="h-10 rounded-lg border-gray-200" placeholder="Primary phone" />
                </Form.Item>
                <Form.Item
                  name="phoneSecondary"
                  label="Additional Number"
                  rules={[{ validator: validatePhoneNumber }]}
                >
                  <Input className="h-10 rounded-lg border-gray-200" placeholder="Optional phone" />
                </Form.Item>
              </div>

              <Form.Item
                name="clientEmail"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input className="h-10 rounded-lg border-gray-200" placeholder="client@example.com" />
              </Form.Item>

              <Form.Item
                name="startDate"
                label="Scheduled Date"
                rules={[{ required: true, message: "Please select a start date" }]}
              >
                <DatePicker
                  className="w-full h-10 rounded-lg border-gray-200"
                  suffixIcon={<CalendarOutlined className="text-gray-400" />}
                  placeholder="Select survey date"
                />
              </Form.Item>
            </div>

            {/* Location Section */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-green-50 text-green-600 flex items-center justify-center font-bold text-sm">2</div>
                <h2 className="text-lg font-bold text-gray-800">Survey Location</h2>
              </div>

              <Form.Item
                name="address"
                label="Complete Address"
                rules={[
                  { required: true, message: "Please enter address" },
                  { min: 5, message: "Address must be at least 5 characters long" },
                ]}
              >
                <Input.TextArea
                  rows={2}
                  className="rounded-lg border-gray-200 focus:border-[#1955EB] transition-all"
                  placeholder="Street name, building, floor etc."
                />
              </Form.Item>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true, message: "Please select state" }]}
                >
                  <Select
                    placeholder="Select State"
                    className="h-10 rounded-lg custom-select"
                    onChange={handleStateChange}
                  >
                    {states.map((state) => (
                      <Option key={state.isoCode} value={state.isoCode}>{state.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="district"
                  label="District"
                  rules={[{ required: true, message: "Please select district" }]}
                >
                  <Select
                    placeholder="Select District"
                    className="h-10 rounded-lg"
                    onChange={handleDistrictChange}
                    disabled={!selectedState}
                  >
                    {districts.map((district) => (
                      <Option key={district.name} value={district.name}>{district.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  name="city"
                  label="City"
                  rules={[{ required: true, message: "Please select city" }]}
                >
                  <Select
                    placeholder="Select City"
                    className="h-10 rounded-lg"
                    disabled={!selectedDistrict}
                    onChange={handleCityChange}
                  >
                    {cities.map((city) => (
                      <Option key={city.name} value={city.name}>{city.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="locationUrl"
                  label="Google Maps URL"
                  rules={[
                    { required: true, message: "Please enter map URL" },
                    { validator: validateGoogleMapUrl },
                  ]}
                >
                  <Input className="h-10 rounded-lg border-gray-200" placeholder="https://google.com/maps/..." />
                </Form.Item>
              </div>

              <Form.Item
                name="instructions"
                label="General Instructions"
              >
                <Input className="h-10 rounded-lg border-gray-200" placeholder="Any special notes for the surveyor" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CreateSurvey;
